import React, { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import api from "@/lib/api";

const KINDS = ["daily_7am", "welcome", "birthday", "magic_link"];
const LABEL = { daily_7am: "Daily 7am", welcome: "Welcome", birthday: "Birthday", magic_link: "Sign-in link" };

const Cell = ({ k }) => (
  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
    {k ? (
      <>
        <span className="text-ink font-semibold" data-testid={`stat-sent-${k.kind}`}>{k.sent} sent</span>
        <span className={`ml-2 ${k.failed ? "text-red-600" : "text-ink-muted"}`} data-testid={`stat-failed-${k.kind}`}>{k.failed} failed</span>
        <span className="ml-2 text-ink-muted">· open {k.open_rate == null ? "n/a" : `${k.open_rate}%`}</span>
      </>
    ) : <span className="text-[#d1d5db]">—</span>}
  </td>
);

export const EmailStatsPanel = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/email-stats", { params: { days: 14 } }).then((r) => setStats(r.data)).catch(() => setStats({ rows: [], totals: {} })); }, []);

  return (
    <section className="mb-10" data-testid="email-stats-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-base md:text-lg font-bold text-ink flex items-center gap-2"><Mail className="w-4 h-4 text-gold" /> Email sends — last 14 days</h2>
        <p className="font-mono text-[11px] text-ink-muted">Open rate not exposed by the managed sender (shown as n/a)</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {KINDS.map((k) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`stat-total-${k}`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{LABEL[k]}</p>
            <p className="mt-1 text-2xl font-extrabold text-ink">{stats?.totals?.[k]?.sent ?? 0}<span className="text-xs font-mono font-normal text-ink-muted ml-1">sent</span></p>
            <p className={`font-mono text-xs ${stats?.totals?.[k]?.failed ? "text-red-600" : "text-ink-muted"}`}>{stats?.totals?.[k]?.failed ?? 0} failed</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="email-stats-table">
          <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-ink-muted">
            <tr><th className="px-4 py-3">Date</th>{KINDS.map((k) => <th key={k} className="px-4 py-3">{LABEL[k]}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!stats ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-xs text-ink-muted">Loading…</td></tr>
            ) : stats.rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-xs text-ink-muted" data-testid="email-stats-empty">No emails sent in this window.</td></tr>
            ) : stats.rows.map((r) => (
              <tr key={r.date} data-testid={`email-stats-row-${r.date}`}>
                <td className="px-4 py-3 font-mono text-xs text-ink font-semibold">{r.date}</td>
                {KINDS.map((k) => <Cell key={k} k={r.kinds.find((x) => x.kind === k)} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
